import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { EvaluationService } from '@services/core/evaluation.service';
import { EntityService } from '@services/core/entity.service';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MONTH_NAMES_ES, buildYearsBack, padMonth } from '@shared/period.utils';
import { confirmDialog, promptOpenPeriod, promptReopenPeriod, promptEditPeriod, successToast, errorDialog } from './period-dialogs';
import { buildPeriodRows, canOpenPeriods, closeManyText, collectIds, collectOpenIds, dialogErrorText, entityList, firstGroup, groupsOf, hasExpired, isDisabledPeriod, isEmptyList, isNoPeriodRow, isReviewable, loadErrorText, periodList, periodRealIds, reopenEntityIds, resetEntityIds, resetText, resultMessage, selectedIdList, selectedRows, statusBadgeOf, statusLabel } from './periods-helpers';

@Component({
  selector: 'app-periods',
  templateUrl: './periods.component.html',
  styleUrls: ['./periods.component.scss'],
  imports: [PanelComponent, FormsModule, NgSelectComponent, DatePipe],
  providers: [EvaluationService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class PeriodsComponent implements OnInit {
  protected module: any;
  protected routeSegment = '';
  protected availableRoutes: string[] = [];
  protected groups: any[] = [];
  protected selectedGroup: any;
  protected years = buildYearsBack(2024);
  protected yearSelected = new Date().getFullYear();
  protected months = MONTH_NAMES_ES.map((name, i) => ({ name, value: i + 1 }));
  protected monthSelected = new Date().getMonth() + 1;
  protected periods: any[] = [];
  protected selectedIds = new Set<string>();

  private evaluationService = inject(EvaluationService);
  private entityService = inject(EntityService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    const data = this.route.snapshot.data['periodsData'] || {};
    this.routeSegment = data.segment ?? (this.route.snapshot.paramMap.get('moduleRoute') || '');
    this.availableRoutes = data.routes || [];
    this.module = data.module;
    this.loadGroups();
  }

  private loadGroups() {
    this.groups = groupsOf(this.module);
    this.selectedGroup = firstGroup(this.groups);
    this.loadPeriods();
  }

  onFilterChange() {
    this.loadPeriods();
  }

  private periodIso(): string {
    return `${this.yearSelected}-${padMonth(this.monthSelected)}-01`;
  }

  protected monthLabel(): string {
    return `${MONTH_NAMES_ES[this.monthSelected - 1]} ${this.yearSelected}`;
  }

  private loadPeriods() {
    if (canOpenPeriods(this.module, this.selectedGroup) === false) return;
    this.selectedIds = new Set<string>();
    this.loadMonthData();
  }

  private loadMonthData() {
    forkJoin({
      periods: this.evaluationService.getPeriods(this.module.id, this.periodIso(), this.selectedGroup),
      entities: this.entityService.getEntitiesGroup(this.selectedGroup),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res: any) => this.applyRows(res), error: (e: any) => this.loadError(e) });
  }

  private loadError(e: any) {
    console.error('Periodos: error cargando datos', e);
    errorDialog(loadErrorText(e));
  }

  private applyRows(res: any) {
    this.periods = buildPeriodRows(periodList(res), entityList(res));
  }

  protected canOpen(): boolean {
    return canOpenPeriods(this.module, this.selectedGroup);
  }

  protected isOpen(p: any): boolean {
    if (isDisabledPeriod(p) === true) return false;
    return hasExpired(p) === false;
  }

  protected statusFor(p: any): string {
    return statusLabel(p);
  }

  protected statusBadge(p: any): string {
    return statusBadgeOf(p);
  }

  protected toggleSelection(p: any) {
    if (this.selectedIds.has(p.id)) {
      this.selectedIds.delete(p.id);
      return;
    }
    this.selectedIds.add(p.id);
  }

  protected openIds(): string[] {
    return collectOpenIds(this.periods, (p: any) => this.isOpen(p));
  }

  protected hasOpen(): boolean {
    return this.openIds().length > 0;
  }

  protected isAllSelected(): boolean {
    if (this.periods.length === 0) return false;
    return this.selectedIds.size === this.periods.length;
  }

  protected isIndeterminate(): boolean {
    if (this.selectedIds.size === 0) return false;
    return this.selectedIds.size < this.periods.length;
  }

  protected toggleAll() {
    if (this.isAllSelected() === true) return this.clearSelection();
    this.selectedIds = new Set<string>(collectIds(this.periods));
  }

  private clearSelection() {
    this.selectedIds = new Set<string>();
  }

  protected async openPeriods() {
    const res = await promptOpenPeriod(this.monthLabel());
    if (!res) return;
    this.runAction(this.evaluationService.openPeriod(this.selectedGroup.id, this.module.id, res.expirationDate, this.periodIso(), undefined, res.force), 'Periodos abiertos');
  }

  protected closeSelected() {
    if (isEmptyList(selectedIdList(this.selectedIds)) === true) return;
    this.closeReal(periodRealIds(selectedIdList(this.selectedIds)));
  }

  private closeReal(ids: string[]) {
    if (isEmptyList(ids) === true) return errorDialog('Seleccione al menos un periodo existente para cerrar.');
    this.closeMany(ids);
  }

  protected closeAllOpen() {
    this.closeMany(this.openIds());
  }

  private closeMany(ids: string[]) {
    confirmDialog('Cerrar periodos', closeManyText(ids.length), 'Cerrar').then((ok) => this.onCloseConfirm(ok, ids));
  }

  private onCloseConfirm(ok: boolean, ids: string[]) {
    if (ok === true) this.runAction(this.evaluationService.closePeriods(ids), 'Periodos cerrados');
  }

  protected async reopenSelected() {
    if (isEmptyList(selectedIdList(this.selectedIds)) === true) return;
    this.reopenChosen(selectedRows(this.periods, this.selectedIds));
  }

  private async reopenChosen(rows: any[]) {
    const entityIds = reopenEntityIds(rows);
    if (isEmptyList(entityIds) === true) return errorDialog('Seleccione al menos una entidad para reabrir.');
    this.sendReopen(entityIds);
  }

  private async sendReopen(entityIds: string[]) {
    const exp = await promptReopenPeriod(entityIds.length, this.monthLabel());
    if (exp) this.runAction(forkJoin(entityIds.map((id) => this.reopenSource(id, exp))), 'Periodos reabiertos');
  }

  private reopenSource(id: string, exp: string) {
    return this.evaluationService.openPeriod(this.selectedGroup.id, this.module.id, exp, this.periodIso(), id, true);
  }

  protected async resetSelectedEntities() {
    if (isEmptyList(selectedIdList(this.selectedIds)) === true) return;
    this.resetChosen(selectedRows(this.periods, this.selectedIds));
  }

  private async resetChosen(rows: any[]) {
    const entityIds = resetEntityIds(rows);
    if (isEmptyList(entityIds) === true) return errorDialog('Seleccione al menos una entidad para reiniciar.');
    this.sendReset(entityIds);
  }

  private async sendReset(entityIds: string[]) {
    const ok = await confirmDialog('Reiniciar desde 0', resetText(entityIds.length, this.monthLabel()), 'Reiniciar');
    if (ok === true) this.runAction(this.evaluationService.resetEntityEvaluation(entityIds, this.module.id), 'Evaluación reiniciada');
  }

  protected reviewPeriod(p: any) {
    if (isReviewable(p) === false) return;
    this.router.navigate([`/${this.routeSegment}/evaluacion`], { queryParams: { periodId: p.id, review: 1 } });
  }

  protected async editPeriod(p: any) {
    if (isNoPeriodRow(p) === true) return;
    this.applyEdit(p, await promptEditPeriod(p));
  }

  private applyEdit(p: any, res: { score: number; expiration: string } | null) {
    if (res) this.runAction(this.evaluationService.updatePeriod(p.id, res.score, undefined, res.expiration), 'Periodo actualizado');
  }

  private runAction(source: Observable<any>, fallbackMsg: string) {
    source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r: any) => this.onActionDone(r, fallbackMsg),
      error: (e: any) => this.onActionError(e),
    });
  }

  private onActionDone(r: any, fallbackMsg: string) {
    successToast(resultMessage(r, fallbackMsg));
    this.loadPeriods();
  }

  private onActionError(e: any) {
    errorDialog(dialogErrorText(e));
  }
}
